import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  getPartnerAdminStudents,
  getPartnerAdminStudentById,
  classifyPartnerStudent,
  getPartnerAdminStatistics,
  getPartnerAdminPartners,
} from '../../api/adminApi';
import { useToast } from '../../contexts/ToastContext';

const PartnerStudentsManagement = () => {
  const { showToast } = useToast();

  // State
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 0, limit: 25, total: 0 });
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ userType: '', partnerId: '' });
  const [partners, setPartners] = useState([]);
  const [statistics, setStatistics] = useState(null);

  // Dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [classifyDialogOpen, setClassifyDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetails, setStudentDetails] = useState(null);
  const [classifyUserType, setClassifyUserType] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch students
  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page + 1,
        limit: pagination.limit,
      };
      if (search) params.search = search;
      if (filters.userType) params.userType = filters.userType;
      if (filters.partnerId) params.partnerId = filters.partnerId;

      const response = await getPartnerAdminStudents(params);
      setStudents(response.data.students || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || 0,
      }));
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to fetch students', 'error');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, filters, showToast]);

  // Fetch partners for filter dropdown
  const fetchPartners = useCallback(async () => {
    try {
      const response = await getPartnerAdminPartners({ limit: 100 });
      setPartners(response.data.partners || []);
    } catch (error) {
      console.error('Failed to fetch partners:', error);
    }
  }, []);

  // Fetch statistics
  const fetchStatistics = useCallback(async () => {
    try {
      const response = await getPartnerAdminStatistics();
      setStatistics(response.data.summary || null);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  }, []);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  useEffect(() => {
    fetchPartners();
    fetchStatistics();
  }, [fetchPartners, fetchStatistics]);

  // Handle view student details
  const handleViewStudent = async (student) => {
    setSelectedStudent(student);
    setViewDialogOpen(true);
    try {
      const response = await getPartnerAdminStudentById(student._id);
      setStudentDetails(response.data);
    } catch (error) {
      showToast('Failed to fetch student details', 'error');
    }
  };

  // Handle classify dialog
  const handleOpenClassifyDialog = (student) => {
    setSelectedStudent(student);
    setClassifyUserType(student.userType || '');
    setClassifyDialogOpen(true);
  };

  // Handle classify student
  const handleClassifyStudent = async () => {
    if (!classifyUserType) {
      showToast('Please select a user type', 'error');
      return;
    }

    setActionLoading(true);
    try {
      await classifyPartnerStudent(selectedStudent._id, classifyUserType);
      showToast(`Student classified as ${classifyUserType}`, 'success');
      setClassifyDialogOpen(false);
      fetchStudents();
      fetchStatistics();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to classify student', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle search
  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      setPagination(prev => ({ ...prev, page: 0 }));
      fetchStudents();
    }
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Get user type chip
  const getUserTypeChip = (userType) => {
    if (!userType) {
      return <Chip label="Unclassified" size="small" color="warning" variant="outlined" />;
    }
    return (
      <Chip
        label={userType}
        size="small"
        color={userType === 'INTERNAL' ? 'primary' : 'secondary'}
      />
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Partner Students
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage students attributed to your partners
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            fetchStudents();
            fetchStatistics();
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon color="primary" />
                  <Typography variant="h6">{statistics.totalStudents}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Total Students
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon color="success" />
                  <Typography variant="h6">{statistics.classifiedStudents}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Classified
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon color="primary" />
                  <Typography variant="h6">{statistics.internalStudents}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Internal
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SchoolIcon color="secondary" />
                  <Typography variant="h6">{statistics.externalStudents}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  External
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>User Type</InputLabel>
              <Select
                value={filters.userType}
                label="User Type"
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, userType: e.target.value }));
                  setPagination(prev => ({ ...prev, page: 0 }));
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="INTERNAL">Internal</MenuItem>
                <MenuItem value="EXTERNAL">External</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Partner</InputLabel>
              <Select
                value={filters.partnerId}
                label="Partner"
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, partnerId: e.target.value }));
                  setPagination(prev => ({ ...prev, page: 0 }));
                }}
              >
                <MenuItem value="">All Partners</MenuItem>
                {partners.map((partner) => (
                  <MenuItem key={partner._id} value={partner._id}>
                    {partner.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                setPagination(prev => ({ ...prev, page: 0 }));
                fetchStudents();
              }}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Students Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Student</TableCell>
              <TableCell>Partner</TableCell>
              <TableCell>User Type</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Classified By</TableCell>
              <TableCell>Attributed At</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : students.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No students found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              students.map((student) => (
                <TableRow key={student._id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {student.userId?.firstName} {student.userId?.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {student.userId?.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={student.partnerId?.name || 'N/A'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{getUserTypeChip(student.userType)}</TableCell>
                  <TableCell>
                    <Chip
                      label={student.source === 'PARTNER_LINK' ? 'Link' : 'Admin'}
                      size="small"
                      variant="outlined"
                      color={student.source === 'PARTNER_LINK' ? 'info' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {student.classifiedByUserId ? (
                      <Typography variant="body2">
                        {student.classifiedByUserId.firstName} {student.classifiedByUserId.lastName}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Not classified
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(student.createdAt)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => handleViewStudent(student)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Classify Student">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenClassifyDialog(student)}
                      >
                        {student.userType ? <CheckCircleIcon fontSize="small" /> : <HelpOutlineIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page}
          onPageChange={(_, newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
          rowsPerPage={pagination.limit}
          onRowsPerPageChange={(e) =>
            setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 0 }))
          }
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      {/* View Student Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Student Details
        </DialogTitle>
        <DialogContent dividers>
          {studentDetails ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Student Information</Typography>
                <Box sx={{ mt: 1 }}>
                  <Typography><strong>Name:</strong> {studentDetails.attribution?.userId?.firstName} {studentDetails.attribution?.userId?.lastName}</Typography>
                  <Typography><strong>Email:</strong> {studentDetails.attribution?.userId?.email}</Typography>
                  <Typography><strong>Country:</strong> {studentDetails.attribution?.userId?.country || 'N/A'}</Typography>
                  <Typography><strong>Phone:</strong> {studentDetails.attribution?.userId?.phone || 'N/A'}</Typography>
                  <Typography><strong>Registered:</strong> {formatDate(studentDetails.attribution?.userId?.createdAt)}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Partner Attribution</Typography>
                <Box sx={{ mt: 1 }}>
                  <Typography><strong>Partner:</strong> {studentDetails.attribution?.partnerId?.name}</Typography>
                  <Typography><strong>Type:</strong> {studentDetails.attribution?.partnerId?.type}</Typography>
                  <Typography><strong>User Type:</strong> {studentDetails.attribution?.userType || 'Unclassified'}</Typography>
                  <Typography><strong>Source:</strong> {studentDetails.attribution?.source}</Typography>
                  <Typography><strong>Attributed:</strong> {formatDate(studentDetails.attribution?.createdAt)}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Recent Registrations ({studentDetails.registrations?.length || 0})
                </Typography>
                {studentDetails.registrations?.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Product</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Score</TableCell>
                          <TableCell>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {studentDetails.registrations.slice(0, 10).map((reg) => (
                          <TableRow key={reg._id}>
                            <TableCell>{reg.productId?.name || reg.productId?.label || 'N/A'}</TableCell>
                            <TableCell>{reg.productId?.examType || 'N/A'}</TableCell>
                            <TableCell>{reg.score?.overAllScore || 'N/A'}</TableCell>
                            <TableCell>{formatDate(reg.createdAt)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info">No registrations found</Alert>
                )}
              </Grid>
            </Grid>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              setViewDialogOpen(false);
              handleOpenClassifyDialog(selectedStudent);
            }}
          >
            Classify Student
          </Button>
        </DialogActions>
      </Dialog>

      {/* Classify Student Dialog */}
      <Dialog open={classifyDialogOpen} onClose={() => setClassifyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Classify Student
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Classify <strong>{selectedStudent?.userId?.firstName} {selectedStudent?.userId?.lastName}</strong> as Internal or External student.
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Internal:</strong> Students enrolled in your courses/programs<br />
              <strong>External:</strong> Students who registered via your link but are not enrolled
            </Alert>
            <FormControl fullWidth>
              <InputLabel>User Type</InputLabel>
              <Select
                value={classifyUserType}
                label="User Type"
                onChange={(e) => setClassifyUserType(e.target.value)}
              >
                <MenuItem value="INTERNAL">Internal</MenuItem>
                <MenuItem value="EXTERNAL">External</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClassifyDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleClassifyStudent}
            disabled={actionLoading || !classifyUserType}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Classify'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartnerStudentsManagement;
